import React, { useEffect, useState } from 'react';
import { Card, Table, Row, Col, Form, Button, Alert, Badge } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import UserLayout from '../../components/user/UserLayout';
import api from '../../lib/api';
import { useToast } from '../../components/common/ToastProvider';

export default function UserChatHistoryPage() {
  const [email, setEmail] = useState('');
  const [threadId, setThreadId] = useState('');
  const [channel, setChannel] = useState('');
  const [history, setHistory] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [msg, setMsg] = useState('');
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  const load = async () => {
    try {
      const params = { email, thread_id: threadId };
      if (channel) params.channel = channel;

      const res = await api.get('/user/chat-history/', { params });
      setHistory(res.data.history || []);
      setHandovers(res.data.handovers || []);
    } catch (error) {
      setMsg('Unable to load chat history.');
      showToast('Unable to load chat history.', 'danger');
    }
  };

  useEffect(() => {
    const queryThreadId = (searchParams.get('thread_id') || '').trim();
    const queryChannel = (searchParams.get('channel') || '').trim();
    const queryEmail = (searchParams.get('email') || '').trim();
    if (queryThreadId) setThreadId(queryThreadId);
    if (queryChannel) setChannel(queryChannel);
    if (queryEmail) setEmail(queryEmail);
  }, [searchParams]);

  useEffect(() => {
    load();
  }, [threadId, channel, email]);

  const requestHandover = async () => {
    try {
      const res = await api.post('/user/chat-handover/', {
        channel: channel || 'widget',
        email,
        thread_id: threadId,
        message: 'I want to chat with a customer agent',
      });
      setMsg(res.data?.message || 'Handover requested');
      load();
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Unable to request handover');
      showToast(e?.response?.data?.error || 'Unable to request handover', 'danger');
    }
  };

  return (
    <UserLayout
      title="Chat History"
      subtitle="Review conversations and request live agent handover"
    >
      {msg && <Alert variant="info">{msg}</Alert>}

      <Card className="user-card p-4 mb-4">
        <div className="user-section-title">Filters</div>

        <Row className="g-3">
          <Col lg={3}>
            <Form.Control
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Filter by email"
            />
          </Col>

          <Col lg={3}>
            <Form.Control
              value={threadId}
              onChange={(e) => setThreadId(e.target.value)}
              placeholder="Filter by thread ID"
            />
          </Col>

          <Col lg={2}>
            <Form.Select value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="">All Channels</option>
              <option value="widget">Widget</option>
              <option value="whatsapp">WhatsApp</option>
            </Form.Select>
          </Col>

          <Col lg={2}>
            <Button className="w-100 user-quick-action" onClick={load}>
              Refresh
            </Button>
          </Col>

          <Col lg={2}>
            <Button
              className="w-100 user-quick-action"
              variant="warning"
              onClick={requestHandover}
            >
              Request Agent
            </Button>
          </Col>
        </Row>
      </Card>

      <Card className="user-card p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="user-section-title mb-0">Handover Queue</div>
          <Badge bg="primary" pill>
            {handovers.length}
          </Badge>
        </div>

        <div className="table-responsive user-table-wrap">
          <Table hover>
            <thead>
              <tr>
                <th>Channel</th>
                <th>Status</th>
                <th>Agent</th>
                <th>Requester</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {handovers.map((h) => (
                <tr key={h.id}>
                  <td className="text-capitalize">{h.channel}</td>
                  <td>
                    <Badge bg={h.status === 'pending' ? 'warning' : 'success'}>
                      {h.status}
                    </Badge>
                  </td>
                  <td>{h.picked_by_name || 'Pending'}</td>
                  <td>{h.requester_name || h.requester_email || 'Guest'}</td>
                  <td>{new Date(h.created_at).toLocaleString()}</td>
                </tr>
              ))}

              {!handovers.length && (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">
                    No handover requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      <Card className="user-card p-4">
        <div className="user-section-title">Chat Messages</div>

        <div className="table-responsive user-table-wrap">
          <Table hover>
            <thead>
              <tr>
                <th>Thread</th>
                <th>Email</th>
                <th>Question</th>
                <th>Response</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td>{h.thread_id}</td>
                  <td>{h.email || '-'}</td>
                  <td>{h.question}</td>
                  <td>{h.response}</td>
                  <td>{new Date(h.created_at).toLocaleString()}</td>
                </tr>
              ))}

              {!history.length && (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">
                    No chat history found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>
    </UserLayout>
  );
}
